import { Close, Search } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import Moment from "moment";
import { useCallback, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import service from "../../services/ticket";
import config from "../../utils/config";
import queryDefault from "../../utils/queryDefault";

const NoOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Nothing to show
    </Typography>
  </Box>
);

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

// Mobile Card Component
interface TicketCardProps {
  ticket: {
    id: number;
    code: string;
    status: string;
    name?: string;
    email?: string;
    phone?: string;
    booked_at?: string;
  };
  onReset: (id: number) => void;
  onResend: (id: number) => void;
}

const TicketCard = ({ ticket, onReset, onResend }: TicketCardProps) => (
  <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
    <CardContent>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {ticket.code}
          </Typography>
          <Chip
            label={ticket.status}
            color={
              ticket.status === "sold"
                ? "success"
                : ticket.status === "booked"
                  ? "warning"
                  : "default"
            }
            size="small"
          />
        </Stack>

        <Divider />

        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Order Details:
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2">Name: {ticket.name || "-"}</Typography>
            <Typography variant="body2">
              Email: {ticket.email || "-"}
            </Typography>
            <Typography variant="body2">
              Phone: {ticket.phone || "-"}
            </Typography>
          </Stack>
        </Stack>

        {ticket.booked_at && (
          <Typography variant="body2" color="text.secondary">
            Booked at: {Moment(ticket.booked_at).format("DD/MM/YYYY HH:mm")}
          </Typography>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {ticket.status === "booked" && (
            <Tooltip title="Reset ticket">
              <IconButton
                size="small"
                color="info"
                onClick={() => onReset(ticket.id)}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
          {ticket.status === "sold" && (
            <Tooltip title="Resend ticket">
              <IconButton
                size="small"
                color="info"
                onClick={() => onResend(ticket.id)}
              >
                <EmailIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export default function Ticket(params: any) {
  const { id } = params;
  const notification = useNotification();
  const [options, setOptions] = useState<any>({
    category_id: id,
    keyword: "",
  });
  const [pageSize, setPageSize] = useState<number>(10);

  // Add these new state handlers
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [mobileTickets, setMobileTickets] = useState<any[]>([]);

  // Modify the onHandleSearch function
  const onHandleSearch = useCallback((values: { keyword: any }) => {
    // Reset page to 1 and clear mobile tickets when search changes
    setPage(1);
    setMobileTickets([]);
    setOptions((prev: any) => ({
      ...prev,
      keyword: values.keyword,
    }));
  }, []);

  // Update the listTicket query with better error handling and loading states
  const listTicket = useQuery(
    ["list-ticket", { ...options, page, pageSize }],
    ({ queryKey }) => {
      const [_, params] = queryKey;
      return service.getTicketsByCategoryId(params);
    },
    {
      ...queryDefault,
      onSuccess: (data) => {
        if (isMobile) {
          if (page === 1) {
            setMobileTickets(data.data);
          } else {
            setMobileTickets((prev) => [...prev, ...data.data]);
          }
          setHasMore(data.data.length === pageSize);
        }
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to load tickets",
          type: "error",
          position: "top",
        });
        setHasMore(false);
      },
    }
  );

  const resetTicket = useMutation("reset-ticket", service.resetTicket, {
    onSuccess: () => {
      listTicket.refetch();
      notification.onOpen({
        message: "Ticket succesfully reset!",
        type: "success",
        position: "top",
      });
    },
  });

  const resendTicket = useMutation("resend-ticket", service.resendTicket, {
    onSuccess: () => {
      notification.onOpen({
        message: "Ticket succesfully resent!",
        type: "success",
        position: "top",
      });
    },
  });

  const alert = useAlert();

  const onUpdate = (id: number) => {
    alert.onClose();
    resetTicket.mutate(id);
  };

  const onResend = (id: number) => {
    alert.onClose();
    resendTicket.mutate(id);
  };

  const form = useForm({
    defaultValues: {
      keyword: "",
    },
  });

  const onClickReset = (id: number) => {
    alert.onOpen({
      title: "Confirm Reset?",
      message: "Are you sure want to reset ticket?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onUpdate(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const onClickResend = (id: number) => {
    alert.onOpen({
      title: "Confirm Resend?",
      message: "Are you sure want to resend ticket?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Resend",
          onClick: () => onResend(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Add load more handler
  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  // Add a loading state component
  const LoadingOverlay = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 4
      }}
    >
      <CircularProgress size={40} />
    </Box>
  );

  const rowCountRef = useRef(listTicket?.data?.total || 0);

  const rowCount = useMemo(() => {
    if (listTicket?.data?.total !== undefined) {
      rowCountRef.current = listTicket?.data?.total;
    }
    return rowCountRef.current;
  }, [listTicket?.data?.total]);

  // Modify the return JSX for better loading and error handling
  return (
    <Box sx={{ mt: 2 }}>
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Controller
            name="keyword"
            control={form.control}
            render={({ field }) => (
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search tickets..."
                {...field}
                {...config.onEnter(() => form.handleSubmit(onHandleSearch)())}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: field.value && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          field.onChange("");
                          interface Options {
                            category_id: string | number;
                            keyword: string;
                            page_number?: number;
                          }

                          // The modified line:
                          setOptions((prev: Options) => ({
                            ...prev,
                            keyword: "",
                          }));
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            )}
          />
          <Button
            variant="contained"
            onClick={form.handleSubmit(onHandleSearch)}
            sx={{
              minWidth: 100,
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Search
          </Button>
        </Stack>
      </Paper>

      {isMobile ? (
        <>
          <Stack spacing={2}>
            {listTicket.isLoading && page === 1 ? (
              <LoadingOverlay />
            ) : listTicket.isError ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error">
                  Failed to load tickets. Please try again.
                </Typography>
                <Button
                  onClick={() => listTicket.refetch()}
                  startIcon={<RefreshIcon />}
                  sx={{ mt: 1 }}
                >
                  Retry
                </Button>
              </Box>
            ) : (
              <>
                {mobileTickets.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onReset={onClickReset}
                    onResend={onClickResend}
                  />
                ))}
                {mobileTickets.length === 0 && <NoOverlay />}
              </>
            )}
          </Stack>

          {hasMore && !listTicket.isError && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={listTicket.isFetching}
                startIcon={
                  listTicket.isFetching ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  py: 1,
                  px: 4,
                }}
              >
                {listTicket.isFetching ? "Loading..." : "Load More"}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <DataGrid
          autoHeight
          disableColumnMenu
          loading={listTicket.isLoading}
          getRowId={(record) => get(record, "id")}
          rows={get(listTicket, "data.data", [])}
          rowCount={rowCount}
          page={page - 1} // DataGrid uses 0-based pagination
          pageSize={pageSize}
          rowsPerPageOptions={[10, 20, 30, 50]}
          pagination
          paginationMode="server"
          disableSelectionOnClick
          onPageChange={(newPage) => {
            setPage(newPage + 1);
          }}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          components={{
            NoRowsOverlay: NoOverlay,
          }}
          columns={[
            {
              field: "code",
              headerName: "Code",
              flex: 1,
              minWidth: 250,
              sortable: false,
              filterable: false,
              align: "center",
              headerAlign: "center",
            },
            {
              field: "status",
              headerName: "Status",
              flex: 1,
              minWidth: 250,
              sortable: false,
              filterable: false,
              headerAlign: "center",
              align: "center",
            },
            {
              field: "name",
              headerName: "Order",
              flex: 1,
              minWidth: 150,
              sortable: false,
              filterable: false,
              headerAlign: "center",
              align: "center",
              renderCell: (params) => {
                return (
                  <Box sx={{ lineHeight: "15px" }}>
                    {params.row.name ?? "-"}
                    <br />
                    {params.row.email ?? "-"}
                    <br />
                    {params.row.phone ?? "-"}
                  </Box>
                );
              },
            },
            {
              field: "booked_at",
              headerName: "Booked At",
              flex: 1,
              minWidth: 150,
              sortable: false,
              filterable: false,
              headerAlign: "center",
              align: "center",
              renderCell: (params) => {
                return (
                  <>
                    {params.value
                      ? Moment(params.value).format("DD/MM/YYYY hh:mm:ss")
                      : ""}
                  </>
                );
              },
            },
            {
              field: "actions",
              headerName: "Action",
              sortable: false,
              filterable: false,
              width: 200,
              renderCell: (params) => {
                return (
                  <Stack direction="row" spacing={1}>
                    {params.row.status === "booked" && (
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={() => onClickReset(params.row.id)}
                      >
                        Reset
                      </Button>
                    )}
                    {params.row.status === "sold" && (
                      <Button
                        variant="outlined"
                        color="info"
                        onClick={() => onClickResend(params.row.id)}
                      >
                        Resend
                      </Button>
                    )}
                  </Stack>
                );
              },
            },
          ]}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f0f0f0",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "background.neutral",
              borderRadius: "8px 8px 0 0",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              color: "text.primary",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "action.hover",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus": {
              outline: "none",
            },
          }}
        />
      )}
    </Box>
  );
}
